// @flow strict
import * as React from 'react';
import cx from 'classnames';
import { useFormik } from 'formik';
import { Button, Card, LoadingIndicator } from '@ansarada/ace-react';
import { string, object } from 'yup';
import axios from 'axios';
import queryString from 'query-string';

import { DataNDuration, dataPlans, subscriptionPlans } from './Internal/DataNDuration';
import { ContactDetails } from './Internal/ContactDetails';
import { BillingDetails } from './Internal/BillingDetails';
import { QuoteSummary } from './Internal/QuoteSummary';

// eslint-disable-next-line css-modules/no-unused-class
import styles from './styles.scss';
import { SignUpSchema } from '../../core/SignUp';
import PardotDialog from '../../internal/PardotDialog';
import { track } from '../../core/lib/segment-actions';

const pardotFormUrl = 'https://go.ansarada.com/landing-iframe.html?formid=1009';
const pardotFormTitle = 'Enter your details and we will contact you with a custom quote';

type PropTypes = {|
  defaultCurrency: 'AUD' | 'USD' | 'EUR' | 'GBP' | 'ZAR',
  apiURL?: string,
  color?: 'gold' | 'platinum' | 'silver' | 'bronze' | 'partner',
  savings?: number,
  partnerName?: string,
  geolocationCode?: string,
|};

const config = {
  sendGridApiKey: '',
  restrictedEmails: ['intralinks.com'],
  restrictedEmailMessage: 'Please use company emailId',
  noPhone: true,
};
const validationSchemas = [
  object().shape({
    DataPlan: string().required(),
    SubscriptionPlan: string().required(),
  }),
  // object().shape({}),
  SignUpSchema(config),
  object().shape({
    // InvoiceFirstName: string().required('Enter client first name'),
    // InvoiceLastName: string().required('Enter client last name'),
    // InvoiceCompany: string().required('Enter client company name'),
    InvoiceEmail: string().email('Invalid email'),
    // InvoiceAddress: string().required('Enter client address'),
  }),
];

const getValidationSchema = (step, schemas) => schemas[step] || {};

const initialState = {
  Firstname: '',
  Lastname: '',
  Company: '',
  Email: '',
  PhoneNumber: '',
  FullPhoneNumber: '',
  InvoiceFirstName: '',
  InvoiceLastName: '',
  InvoiceCompany: '',
  InvoiceEmail: '',
  InvoiceAddress: '',
  DataPlan: '',
  SubscriptionPlan: '',
  isPhoneValid: false,
};

const DigitalQuote = ({
  defaultCurrency = 'AUD',
  apiURL = 'https://api.dev1.ansarada.com/quoting/get-quote',
  color = 'None',
  partnerName,
  savings,
  geolocationCode,
}: PropTypes) => {
  const footerRef = React.useRef();
  const [step, setStep] = React.useState(0);
  const [showingDialog, setShowingDialog] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  // $FlowFixMe
  const { errors, values, setFieldValue, touched, setFieldTouched, validateForm } = useFormik({
    initialValues: initialState,
    validationSchema: getValidationSchema(step, validationSchemas),
    validateOnMount: true,
  });

  React.useEffect(() => {
    if (window.location.search) {
      const parsed = queryString.parse(window.location.search);
      if (parsed['is-free-tier'] === '1') {
        track('Free Tier Selected');
      }

      if (dataPlans.includes(parsed.DataPlan)) {
        setFieldValue('DataPlan', parsed.DataPlan);
        setFieldTouched('DataPlan', true);
      }
      if (subscriptionPlans.includes(parsed.SubscriptionPlan)) {
        setFieldValue('SubscriptionPlan', parsed.SubscriptionPlan);
        setFieldTouched('SubscriptionPlan', true);
      }

      // It seems setFieldValue doesn't update value immediately just like React setState.
      setTimeout(() => {
        validateForm();
      }, 0);

      if (
        dataPlans.includes(parsed.DataPlan) &&
        subscriptionPlans.includes(parsed.SubscriptionPlan)
      ) {
        setStep(1);
      }
    }
  }, []);

  const fieldStatusByName = name => (touched[name] && errors[name] ? 'Bad' : 'Good');
  const setFieldTouchedByName = name => () => setFieldTouched(name, true);
  const redirect = () => {
    setTimeout(() => {
      window.location.href = 'https://ansarada.com/quote-thankyou';
    }, 300);
  };

  const downloadQuote = React.useCallback(
    ({
      Firstname,
      Lastname,
      Company,
      Email,
      FullPhoneNumber,
      InvoiceAddress,
      InvoiceCompany,
      InvoiceFirstName,
      InvoiceLastName,
      InvoiceEmail,
      DataPlan,
      SubscriptionPlan,
    }) => {
      setIsLoading(true);
      axios
        .post(apiURL, {
          FirstName: Firstname,
          LastName: Lastname,
          Company,
          Email,
          PhoneNumber: FullPhoneNumber,
          InvoiceCompany,
          InvoiceAddress,
          InvoiceEmail,
          DataPlan,
          SubscriptionPlan,
          InvoiceFirstName,
          InvoiceLastName,
          Currency: defaultCurrency,
          CountryCode: geolocationCode,
          Partnership: color.substring(0, 1).toUpperCase() + color.substring(1),
          PartnerName: partnerName,
        })
        .then(redirect)
        .catch(redirect);
    },
    [],
  );

  const onNextStep = () => {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
    setStep(step + 1);
  };

  const steps = [
    {
      title: 'Get a quote and a free data room',
      text: `Usage fees only start when your deal goes live by adding an external guest user`,
      content: (
        <DataNDuration
          values={values}
          setFieldValue={setFieldValue}
          setFieldTouchedByName={setFieldTouchedByName}
          validateForm={validateForm}
          setShowingDialog={setShowingDialog}
          footerRef={footerRef}
        />
      ),
    },
    {
      title: 'Enter your details',
      text: 'Enter your details so we can set up your free data room and send you the quote',
      content: (
        <ContactDetails
          values={values}
          setFieldValue={setFieldValue}
          setFieldTouchedByName={setFieldTouchedByName}
          fieldStatusByName={fieldStatusByName}
          touched={touched}
          validateForm={validateForm}
          errors={errors}
        />
      ),
    },
    {
      title: 'Enter your client details into the quote',
      text: `Enter your client details you want in the quote or skip this step if it's confidential`,
      content: (
        <BillingDetails
          values={values}
          setFieldValue={setFieldValue}
          touched={touched}
          errors={errors}
          validateForm={validateForm}
          fieldStatusByName={fieldStatusByName}
          setFieldTouchedByName={setFieldTouchedByName}
        />
      ),
    },
    {
      title: 'Your quote is ready!',
      text: `Look out for your free data room set up email`,
      content: <QuoteSummary values={values} />,
    },
  ];

  const stepCount = steps.length - 1;
  const isClientDetailsStep = step + 1 === stepCount;

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h1 className={styles.title}>{steps[step].title}</h1>
        <p className={styles.subTitle}>{steps[step].text}</p>
      </div>
      {isClientDetailsStep && (
        <div className={styles.mainButton}>
          <Button onClick={onNextStep}>Skip this step</Button>
        </div>
      )}
      <div className={styles.container}>
        {savings && savings > 0 ? (
          <span
            className={cx([
              styles.savings,
              color === 'gold' && styles.savingsGold,
              color === 'platinum' && styles.savingsPlatinum,
              color === 'silver' && styles.savingsSilver,
              color === 'bronze' && styles.savingsBronze,
            ])}
          >
            {`Save ${savings}%`}
          </span>
        ) : null}
        <Card className={isClientDetailsStep ? cx(styles.card, styles.clientCard) : styles.card}>
          {step < steps.length - 1 && (
            <div className={styles.steps}>
              <span className={styles.layoutTitle}>
                Step {step + 1} of {steps.length - 1}
              </span>
            </div>
          )}

          <div>{steps[step].content}</div>

          <div className={styles.footer} ref={footerRef}>
            {step > 0 && <Button onClick={() => setStep(step - 1)}>Back</Button>}
            {step < stepCount && (
              <Button
                variant="Primary"
                disabled={Object.keys(errors).length > 0}
                onClick={onNextStep}
              >
                {isClientDetailsStep ? 'Review Details' : 'Next'}
              </Button>
            )}
            {step === stepCount && (
              <Button variant="Primary" onClick={() => downloadQuote(values)} disabled={isLoading}>
                {/* $FlowFixMe */}
                {isLoading ? <LoadingIndicator size="Small" /> : 'Email me the quote'}
              </Button>
            )}
          </div>
        </Card>
        {showingDialog && (
          <PardotDialog
            formTitle={pardotFormTitle}
            formUrl={pardotFormUrl}
            onToggle={() => setShowingDialog(false)}
          />
        )}
      </div>
    </div>
  );
};

DigitalQuote.defaultProps = {
  defaultCurrency: 'AUD',
};

export default DigitalQuote;
